/**
 * Tracking utility for pushing events to dataLayer
 * Respects user consent from localStorage
 * Enforces lower_snake_case naming convention
 * Prevents PII/sensitive data in payloads
 *
 * Usage:
 *   import { pushEvent } from '../utils/tracking'
 *   pushEvent('search_results', { search_term: 'test', result_count: 5 })
 */

let isPageContextReady = false
let queuedFormStepEvents: Record<string, any>[] = []

export const beginPageContextTracking = (): void => {
	isPageContextReady = false
	queuedFormStepEvents = []
}

export const completePageContextTracking = (): void => {
	isPageContextReady = true
	const queuedEvents = queuedFormStepEvents
	queuedFormStepEvents = []

	queuedEvents.forEach((eventData) => pushEvent('form_step_view', eventData))
}


/**
 * Checks if user has given consent for analytics
 */
const validateEventFormat = (
	eventName: string,
	eventData: Record<string, any>
): boolean => {
	// Check event name
	if (!/^[a-z_]+$/.test(eventName)) {
		console.error(
			`[Tracking] Event name "${eventName}" must be in lower_snake_case format`
		)
		return false
	}

	// Check all keys in payload
	for (const key of Object.keys(eventData)) {
		if (!/^[a-z_]+$/.test(key)) {
			console.error(
				`[Tracking] Event property "${key}" must be in lower_snake_case format (in event: "${eventName}")`
			)
			return false
		}
	}

	return true
}

/**
 * Checks for potential PII/sensitive data in payload
 * Returns true if sensitive data detected (should NOT push event)
 */
const containsSensitiveData = (eventData: Record<string, any>): boolean => {
	const sensitiveKeyPatterns = [
		/email/i,
		/phone/i,
		/address/i,
		/postal_code/i,
		/zip/i,
		/user_id/i,
		/user_email/i,
		/ssn/i,
		/birth/i,
		/password/i,
		/token/i,
		/api[_-]?key/i,
		/secret/i,
		/credit_card/i,
		/card_number/i
	]

	for (const key of Object.keys(eventData)) {
		for (const pattern of sensitiveKeyPatterns) {
			if (pattern.test(key)) {
				console.error(`[Tracking] Sensitive key detected in payload: "${key}"`)
				return true
			}
		}
	}

	// Check values for email-like patterns
	for (const value of Object.values(eventData)) {
		if (typeof value === 'string') {
			if (/@/.test(value) && /\./.test(value)) {
				console.error(
					`[Tracking] Potential email detected in payload: "${value}"`
				)
				return true
			}
		}
	}

	return false
}

/**
 * Main function to push event to dataLayer
 * Only pushes if:
 * - User has given consent
 * - Event name is valid (lower_snake_case)
 * - No sensitive data detected
 * - GTM container is initialized
 *
 * @param eventName - Name of event (must be lower_snake_case)
 * @param eventData - Event payload object (all keys must be lower_snake_case)
 */
export const pushEvent = (
	eventName: string,
	eventData: Record<string, any> = {}
): void => {
	
	// Check if running in browser
	if (typeof window === 'undefined') {
		console.warn('[Tracking] pushEvent called on server-side, skipping')
		return
	}

	// No need to validate consent here, as the script should be loaded regardless of consent status.
	// if (!hasAnalyticsConsent()) {
	// 	console.warn(
	// 		`[Tracking] Event "${eventName}" not pushed: user has not given consent`
	// 	)
	// 	return
	// }

	// Validate format
	if (!validateEventFormat(eventName, eventData)) {
		console.warn(
			`[Tracking] Event "${eventName}" not pushed: invalid format`
		)
		return
	}

	// Check for sensitive data
	if (containsSensitiveData(eventData)) {
		console.warn(
			`[Tracking] Event "${eventName}" not pushed: sensitive data detected`
		)
		return
	}

	if (eventName === 'form_step_view' && !isPageContextReady) {
		queuedFormStepEvents.push({ ...eventData })
		return
	}

	// Check if dataLayer exists
	if (!window.dataLayer) {
		console.warn('[Tracking] window.dataLayer not initialized, skipping event')
		return
	}

	// Build event object
	const event = {
		event: eventName,
		...eventData,
		timestamp: new Date().getTime()
	}

	// Push to dataLayer
	try {
		window.dataLayer.push(event)
		console.log(`[Tracking] Event pushed: "${eventName}"`, event)

		if (eventName === 'page_context_ready') {
			completePageContextTracking()
		}
	} catch (error) {
		console.error(`[Tracking] Failed to push event "${eventName}":`, error)
	}
}

/**
 * Type-safe event payload builder
 * Use for specific events with known structure
 */
export const createEventPayload = (
	eventName: string,
	data: Record<string, any>
) => {
	return {
		event: eventName,
		...data
	}
}

declare global {
	interface Window {
		dataLayer?: any[]
	}
}
